import React, { useRef, useState, useEffect, ReactNode } from "react";
import { ReactComponent as ArrowCircleIcon } from "@icons/arrow-circle.svg";

interface DesktopCarouselProps {
  children: ReactNode;
  gap?: number;
}

enum CarouselMoveDirection {
  Left,
  Right
}

export const DesktopCarousel = ({
  children,
  gap = 16,
}: DesktopCarouselProps) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const itemsRefs = useRef<(HTMLDivElement | null)[]>([]);

  const count = React.Children.count(children);

  const [visibilityMap, setVisibilityMap] = useState<boolean[]>(
    Array(count).fill(false)
  );

  const [offset, setOffset] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);

  const updateItemWidth = () => {
    const firstItem = itemsRefs.current[0];
    if (firstItem) setItemWidth(firstItem.offsetWidth);
  };

  const itemStepWidth = itemWidth + gap;

  const maxStepsWidth = itemStepWidth * count;

  const carouselWidth = viewportRef.current?.clientWidth || 0;

  const maxOffset = Math.max(0, maxStepsWidth - carouselWidth - gap);

  const canMoveLeft = offset > 0;
  const canMoveRight = offset < maxOffset;

  const moveCarousel = (direction: CarouselMoveDirection) => {
    setOffset((prevOffset) => {
      const newOffset =
        direction === CarouselMoveDirection.Left ? prevOffset - itemStepWidth : prevOffset + itemStepWidth;
      return Math.max(0, Math.min(maxOffset, newOffset));
    });
  };

  useEffect(() => {
    updateItemWidth();
    window.addEventListener("resize", updateItemWidth);
    return () => window.removeEventListener("resize", updateItemWidth);
  }, [children]);

  useEffect(() => {
    const intersectionThreshold = 0.01;
    const root = viewportRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (observedItems) => {
        setVisibilityMap((prev) => {
          const updated = [...prev];
          observedItems.forEach((observedItem) => {
            const index = Number(observedItem.target.getAttribute("data-index"));
            updated[index] = observedItem.intersectionRatio > intersectionThreshold;
          });
          return updated;
        });
      },
      {
        root,
        threshold: intersectionThreshold,
      }
    );

    itemsRefs.current.forEach((item) => {
      if (item) observer.observe(item);
    });

    return () => observer.disconnect();
  }, [children]);

  return (
    <div
      className="relative w-full flex items-center"
      role="region"
      aria-roledescription="carousel"
      aria-label="Carousel"
      aria-live="polite"
    >
      {canMoveLeft && (
        <button
          data-testid="carousel-left-button"
          onClick={() => moveCarousel(CarouselMoveDirection.Left)}
          className="absolute -left-14 z-10 bg-gray-800 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-md"
          aria-label="Move carousel left"
        >
          <ArrowCircleIcon className="rotate-180 text-navy-blue" />
        </button>
      )}

      <div ref={viewportRef} className="w-full" style={{ position: "relative" }}>
        <div
          className="-left-6 min-w-[calc(100%+48px)] p-6 overflow-hidden"
          style={{ height: "100%", position: "relative" }}
        >
          <div
            className="flex flex-nowrap transition-transform duration-300"
            style={{
              columnGap: gap,
              transform: `translateX(-${offset}px)`,
            }}
          >
            {React.Children.map(children, (child, index) => {
              const isVisible = visibilityMap[index];

              return (
                <div
                  key={index}
                  data-testid={`wrapper-${index}`}
                  data-index={index}
                  ref={(el) => (itemsRefs.current[index] = el)}
                  role="group"
                  aria-label={`Slide ${index + 1} of ${count}`}
                  aria-hidden={!isVisible}
                  style={{
                    width: `calc((100% - ${gap * 2}px) / 3)`,
                    flexShrink: 0,
                    visibility: isVisible ? "visible" : "hidden",
                  }}
                >
                  {child}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {canMoveRight && (
        <button
          data-testid="carousel-right-button"
          onClick={() => moveCarousel(CarouselMoveDirection.Right)}
          className="absolute -right-14 z-10 bg-gray-800 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-md"
          aria-label="Move carousel right"
        >
          <ArrowCircleIcon className="text-navy-blue" />
        </button>
      )}
    </div>
  );
};
